// Topbar — sticky bar above main content. Holds the breadcrumb trail
// (clickable, derived from useLocation + a route map) plus a slot for
// page-specific action buttons (set via TopbarActions context).
//
// Mobile: also renders the hamburger that toggles the Sidebar drawer.
//
// Architecture: TopbarActionsProvider lives in Shell so both Topbar and
// nested page content share the same context. Pages push their action
// node via useTopbarActions(); Topbar reads it.

import { Link, useLocation, useParams } from 'react-router-dom';
import { ChevronRight, Menu } from 'lucide-react';
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// ── Page actions slot (context) ────────────────────────────────────

interface TopbarActionsCtxValue {
  actions: ReactNode | null;
  setActions: (node: ReactNode | null) => void;
}

const TopbarActionsContext = createContext<TopbarActionsCtxValue | null>(null);

export function TopbarActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<ReactNode | null>(null);
  const value = useMemo(() => ({ actions, setActions }), [actions]);
  return (
    <TopbarActionsContext.Provider value={value}>
      {children}
    </TopbarActionsContext.Provider>
  );
}

/** Push a node into the Topbar's right-side actions slot. Use from any
 *  page component; the slot is cleared on unmount. */
export function useTopbarActions(node: ReactNode | null): void {
  const ctx = useContext(TopbarActionsContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setActions(node);
    return () => ctx.setActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node]);
}

// ── Breadcrumb derivation ──────────────────────────────────────────

interface Crumb {
  label: string;
  to?: string;
}

interface RouteEntry {
  pattern: RegExp;
  build: (match: RegExpMatchArray) => Crumb[];
}

const ROUTES: RouteEntry[] = [
  { pattern: /^\/$/, build: () => [{ label: 'Overview' }] },
  { pattern: /^\/content\/(.+)$/, build: (m) => [{ label: 'Content', to: '/content' }, { label: m[1] ?? 'Detail' }] },
  { pattern: /^\/content$/, build: () => [{ label: 'Content' }] },
  { pattern: /^\/gbp$/, build: () => [{ label: 'Business Profile' }] },
  { pattern: /^\/aieo$/, build: () => [{ label: 'AI Search' }] },
  { pattern: /^\/audits$/, build: () => [{ label: 'Audits' }] },
  { pattern: /^\/site-health$/, build: () => [{ label: 'Site Health' }] },
  { pattern: /^\/rank-tracker$/, build: () => [{ label: 'Rank Tracker' }] },
  { pattern: /^\/backups$/, build: () => [{ label: 'Backups' }] },
  { pattern: /^\/integrations$/, build: () => [{ label: 'Integrations' }] },
  { pattern: /^\/settings$/, build: () => [{ label: 'Settings' }] },
  { pattern: /^\/billing$/, build: () => [{ label: 'Billing' }] },
  { pattern: /^\/operator\/customers\/(.+)$/, build: (m) => [{ label: 'Customers', to: '/operator/customers' }, { label: m[1] ?? 'Detail' }] },
  { pattern: /^\/operator\/customers$/, build: () => [{ label: 'Customers' }] },
  { pattern: /^\/operator\/portfolio$/, build: () => [{ label: 'Portfolio' }] },
  { pattern: /^\/operator\/feed$/, build: () => [{ label: 'Feed' }] },
  { pattern: /^\/operator\/alerts$/, build: () => [{ label: 'Alerts' }] },
];

function deriveCrumbs(pathname: string): Crumb[] {
  for (const route of ROUTES) {
    const match = pathname.match(route.pattern);
    if (match) return route.build(match);
  }
  return [{ label: 'Page' }];
}

// ── Topbar component ───────────────────────────────────────────────

interface TopbarProps {
  onMobileToggle: () => void;
}

export function Topbar({ onMobileToggle }: TopbarProps) {
  const location = useLocation();
  const params = useParams();
  const ctx = useContext(TopbarActionsContext);
  void params; // reserved for future deep routes

  const crumbs = useMemo(() => deriveCrumbs(location.pathname), [location.pathname]);

  return (
    <div className="sticky top-0 z-20 bg-zinc-950/85 backdrop-blur-md border-b border-cream/[0.06]">
      <div className="h-14 px-5 lg:px-8 flex items-center gap-4">
        <button
          type="button"
          onClick={onMobileToggle}
          className="md:hidden p-2 -ml-2 rounded-lg text-cream/70 hover:text-cream hover:bg-cream/[0.06]"
          aria-label="Open navigation"
        >
          <Menu size={18} />
        </button>
        <nav className="flex-1 min-w-0 flex items-center gap-2 text-sm">
          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            const node = crumb.to && !isLast ? (
              <Link
                to={crumb.to}
                className="text-cream/72 hover:text-cream font-semibold transition-colors truncate"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className={`truncate ${
                  isLast ? 'text-cream font-bold' : 'text-cream/72 font-semibold'
                }`}
              >
                {crumb.label}
              </span>
            );
            return (
              <span key={i} className="flex items-center gap-2 min-w-0">
                {node}
                {!isLast && (
                  <ChevronRight size={13} className="text-cream/35 shrink-0" />
                )}
              </span>
            );
          })}
        </nav>
        <div className="flex items-center gap-2 shrink-0">{ctx?.actions ?? null}</div>
      </div>
    </div>
  );
}
